import React from 'react';
import { GitMerge, ShieldCheck, Check, Sparkles } from 'lucide-react';

export const ConsistencySection: React.FC = () => {
  const points = [
    {
      title: 'Tese & Promessa Invioláveis',
      desc: 'O objetivo de transformação pedagógica definido na etapa 01 governa rigidamente a densidade dos capítulos, sem desvios temáticos.'
    },
    {
      title: 'Nível de Profundidade Sincronizado',
      desc: 'Se o produto é voltado para engenheiros seniores, nem o ebook nem a copy comercial caem na armadilha de explicações simplórias ou infantis.'
    },
    {
      title: 'Terminologia Técnica Padronizada',
      desc: 'Conceitos introduzidos no ebook são exatamente os mesmos explorados nos roteiros de VSL, carrosséis e na página de vendas.'
    },
    {
      title: 'Exercícios Alinhados à Teoria',
      desc: 'O Workbook de exercícios referencia diretamente os métodos ensinados no livro principal, garantindo reforço prático imediato.'
    }
  ];

  return (
    <section id="consistencia" className="py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Copy */}
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary px-3 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/20">
              Diferencial Arquitetural
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.15]">
              Um produto único, não uma coleção de arquivos soltos.
            </h2>
            <p className="text-base text-brand-textMuted leading-relaxed">
              O maior defeito ao usar ferramentas de IA tradicionais é a dispersão: o texto do capítulo 1 contradiz o capítulo 5, e a copy de vendas parece escrita para um produto completamente diferente.
            </p>
            <p className="text-base text-brand-textMuted leading-relaxed">
              No InfoForge, todas as camadas compartilham um <strong className="text-white">Grafo de Linhagem Conceitual</strong>. Toda palavra gerada na campanha respeita estritamente o que foi construído no conteúdo técnico.
            </p>

            <div className="space-y-4 pt-4">
              {points.map((pt, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 mt-0.5 border border-emerald-500/20">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">{pt.title}</h4>
                    <p className="text-xs text-brand-textMuted leading-normal">{pt.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Visual Consistency Graph */}
          <div className="lg:col-span-6">
            <div className="p-8 rounded-3xl bg-brand-surface border border-brand-border relative overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between pb-6 border-b border-brand-border mb-6">
                <div className="flex items-center gap-2">
                  <GitMerge className="w-5 h-5 text-brand-cyan" />
                  <span className="text-sm font-bold text-white">Linhagem Conceitual InfoForge</span>
                </div>
                <span className="text-xs font-mono text-brand-cyan px-2.5 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20">
                  Validação Cruzada Ativa
                </span>
              </div>

              {/* Step Flow Visualization */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-brand-bg/80 border border-brand-primary/40 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-mono text-brand-primary uppercase">Âncora Central</span>
                    <div className="text-sm font-bold text-white">Tese do Produto & Promessa Educacional</div>
                  </div>
                  <ShieldCheck className="w-5 h-5 text-brand-primary" />
                </div>

                <div className="w-0.5 h-6 bg-gradient-to-b from-brand-primary to-brand-secondary mx-auto" />

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-brand-bg/60 border border-brand-border">
                    <span className="text-[10px] font-mono text-brand-textSubtle uppercase">Núcleo Editorial</span>
                    <div className="text-xs font-semibold text-white mt-1">Ebook Master & Matriz Curricular</div>
                    <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Fato verificado
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-brand-bg/60 border border-brand-border">
                    <span className="text-[10px] font-mono text-brand-textSubtle uppercase">Didática Prática</span>
                    <div className="text-xs font-semibold text-white mt-1">Workbook & Caderno de Exercícios</div>
                    <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> 100% Sincronizado
                    </div>
                  </div>
                </div>

                <div className="w-0.5 h-6 bg-gradient-to-b from-brand-secondary to-brand-cyan mx-auto" />

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-brand-bg/60 border border-brand-border">
                    <span className="text-[10px] font-mono text-brand-textSubtle uppercase">Campanha Comercial</span>
                    <div className="text-xs font-semibold text-white mt-1">Roteiro de VSL (3 Atos)</div>
                    <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Mesma promessa
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-brand-bg/60 border border-brand-border">
                    <span className="text-[10px] font-mono text-brand-textSubtle uppercase">Página & E-mails</span>
                    <div className="text-xs font-semibold text-white mt-1">Copywriting & Objeções</div>
                    <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Vocabulário idêntico
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-brand-border text-center text-[11px] text-brand-textSubtle">
                Nenhum arquivo órfão. Nenhuma contradição conceitual entre livro e oferta.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
